/* eslint-disable react-hooks/exhaustive-deps */
import { faCircleQuestion } from '@fortawesome/free-regular-svg-icons';
import { faArrowRight, faCircleInfo } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import React, { MouseEventHandler, useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { ReactComponent as InfoIcon } from '../../assets/images/info.svg';
import CustomToolTip from '../../core/CustomToolTip/CustomToolTip';
import * as DO from './DataOptions';

import { useVideo } from '../../context/VideoContext';
import CustomMessage from '../../core/CustomMessage/CustomMessage';
import { formatNumber, numberRegex } from '../../utils/helpers';
import './PriceCalculator.css';

import { getApiContainerType } from '../../api';

import {
	getApiPortsCountries,
	getApiAirportGetAll,
	postApiSeaRatesGetP2P,
	postApiSeaRatesGetD2D,
	postApiSeaRatesGetP2PAir,
	postApiSeaRatesGetD2DAir,
} from '../../api/interconectar-api';
import i18n from '../../i18n';
import CircularLoading from '../../shared/CircularLoading/CircularLoading';
import IconError from '../Icons/IconError';
import IconPro from '../Icons/IconPro';
import { GroupedPorts, GroupedAirports } from './PriceCalculator.types';
import { useAuth } from '../../context/AuthContext';
import { useRemainingCalculations } from '../../hooks/useRemainingCalculations';
import { IconDropDownArrow } from '../Icons';

const PriceCalculatorStepTwo = ({
	handleClickOpen,
	handleContinueClick,
}: {
	handleClickOpen: MouseEventHandler<HTMLDivElement>;
	handleContinueClick: () => void;
}) => {
	const { t } = useTranslation();
	const { setVideoName } = useVideo();
	const [formTwoValues, setFormTwoValues] = useState<any>();
	const [originalFormValues, setOriginalFormValues] = useState<any>(); // Para almacenar los valores originales del localStorage
	const [transportation, setTransportation] = useState('');
	const [validRange, setValidRange] = useState(true);
	const [containers, setContainers] = useState<any[]>([]);
	const [loading, setLoading] = useState<boolean>(true);
	const [error, setError] = useState<string | null>(null);
	const [groupedPorts, setGroupedPorts] = useState<GroupedPorts[]>([]);
	const [groupedAirports, setGroupedAirports] = useState<GroupedAirports[]>([]);
	const [loadingPorts, setLoadingPorts] = useState(false);

	const {
		register,
		handleSubmit,
		setValue,
		reset,
		getValues,
		watch,
		formState: { errors },
	} = useForm();

	const handleChangeMeansTransportation = (e: { target: { value: React.SetStateAction<string> } }) => {
		const newTransportation = e.target.value;
		setTransportation(newTransportation);

		const updatedFormValues = {
			...formTwoValues,
			portDestinationId: formTwoValues?.portDestinationId || '',
			meansTransportation: newTransportation,
			containerId: '',
			internationalFreightValueUsd: '',
			airTotalWeightPlusPackaging: '',
			airTarifCode: '',
		};

		setFormTwoValues(updatedFormValues);
		localStorage.removeItem('formTwo');
	};

	const onSubmit = (data: any) => {
		if (!!data.portDestinationId || !!data.airportId) {
			// Obtener el nombre del puerto desde formTwoValues o buscar en groupedPorts
			let portDestinationName = formTwoValues?.portDestinationName || '';
			let airPortName = formTwoValues?.airPortName || '';
			// Si no tenemos el nombre del puerto/aeropuerto, buscarlo en groupedPorts o groupedAirports
			if (!portDestinationName && !airPortName) {
				if (data.meansTransportation === 'aereo') {
					groupedAirports.forEach((group) => {
						const airport = group.airports.find((a) => a.id === data.airportId);
						if (airport) {
							airPortName =
								(i18n.language === 'es'
									? `${airport.name || ''} ${airport.descriptionEs ? `- ${airport.descriptionEs}` : ''}`.trim()
									: `${airport.name || ''} ${airport.descriptionEn ? `- ${airport.descriptionEn}` : ''}`.trim()) || '';
						}
					});
				} else {
					groupedPorts.forEach((group) => {
						const port = group.ports.find((p) => p.id === data.portDestinationId);
						if (port) {
							portDestinationName =
								(i18n.language === 'es' ? port.descriptionEs || port.name : port.descriptionEn || port.name) || '';
						}
					});
				}
			}

			if (data.meansTransportation === 'maritimo') {
				const maritimo = {
					portDestinationId: data.portDestinationId,
					portDestinationName: portDestinationName,
					meansTransportation: 'Maritime',
					containerId: data.containerId,
					internationalFreightValueUsd: data.internationalFreightValueUsd,
					// Añade los valores extra si existen en formTwoValues
					routeTariffPrice: formTwoValues?.routeTariffPrice,
					co2Amount: formTwoValues?.co2Amount,
					dthcPrice: formTwoValues?.dthcPrice,
					impPrice: formTwoValues?.impPrice,
				};
				localStorage.setItem('formTwo', JSON.stringify(maritimo));
			}

			if (data.meansTransportation === 'multimodal') {
				const multimodal = {
					portDestinationId: data.portDestinationId,
					portDestinationName: portDestinationName,
					meansTransportation: 'Multimodal',
					internationalFreightValueUsd: data.internationalFreightValueUsd,
				};
				localStorage.setItem('formTwo', JSON.stringify(multimodal));
			}

			if (data.meansTransportation === 'aereo') {
				const aereo = {
					airportId: data.airportId,
					airPortName: airPortName,
					meansTransportation: 'Air',
					airTotalWeightPlusPackaging: data.airTotalWeightPlusPackaging,
					airTarifCode: data.airTarifCode,
					internationalFreightValueUsd: data.internationalFreightValueUsd,
					// Añade los valores extra si existen en formTwoValues
					routeTariffPrice: formTwoValues?.routeTariffPrice,
					co2Amount: formTwoValues?.co2Amount,
					dthcPrice: formTwoValues?.dthcPrice,
					impPrice: formTwoValues?.impPrice,
				};
				localStorage.setItem('formTwo', JSON.stringify(aereo));
			}

			handleContinueClick();
		}
	};

	const mapTransportationValue = (value: string): string => {
		switch (value.toLowerCase()) {
			case 'air':
				return 'aereo';
			case 'maritime':
				return 'maritimo';
			case 'multimodal':
				return 'multimodal';
			default:
				return value;
		}
	};

	useEffect(() => {
		try {
			const formTwoStr = localStorage.getItem('formTwo');
			if (formTwoStr) {
				const formTwo = JSON.parse(formTwoStr);
				if (formTwo && typeof formTwo === 'object' && Object.keys(formTwo).length > 0) {
					// Mapear el valor de meansTransportation al formato del select
					const mappedFormTwo = {
						...formTwo,
						meansTransportation: formTwo.meansTransportation ? mapTransportationValue(formTwo.meansTransportation) : '',
					};
					setFormTwoValues(mappedFormTwo);
					setOriginalFormValues(mappedFormTwo); // Guardar los valores originales
					if (mappedFormTwo.meansTransportation) {
						setTransportation(mappedFormTwo.meansTransportation);
					}
				}
			}
		} catch (error) {
			console.error('Error loading form data from localStorage:', error);
			localStorage.removeItem('formTwo');
		}
	}, []);

	useEffect(() => {
		if (formTwoValues) {
			const formData = {
				portDestinationId: formTwoValues.portDestinationId || '',
				portDestinationName: formTwoValues.portDestinationName || '',
				meansTransportation: formTwoValues.meansTransportation || '',
				containerId: formTwoValues.containerId || '',
				internationalFreightValueUsd: formTwoValues.internationalFreightValueUsd || '',
				airTotalWeightPlusPackaging: formTwoValues.airTotalWeightPlusPackaging || '',
				airTarifCode: formTwoValues.airTarifCode || '',
			};
			reset(formData);
		}
	}, [formTwoValues, reset]);

	useEffect(() => {
		if (!!formTwoValues?.meansTransportation) {
			setValue('meansTransportation', formTwoValues?.meansTransportation);
			setTransportation(formTwoValues?.meansTransportation);
		}
	}, [formTwoValues?.meansTransportation]);

	const validateRange =
		!!errors?.internationalFreightValueUsd &&
		(errors?.internationalFreightValueUsd.type === 'min' || errors?.internationalFreightValueUsd.type === 'max');

	useEffect(() => {
		transportation === 'maritimo' || transportation === 'multimodal'
			? setVideoName('secondBarcoVideo')
			: transportation === 'aereo'
				? setVideoName('secondAvionVideo')
				: setVideoName('');
	}, [transportation]);

	useEffect(() => {
		const fetchContainers = async () => {
			try {
				// Llama a la API

				const data = await getApiContainerType();
				if (data.results) {
					setContainers([...data.results]);
				}
			} catch (err) {
				setError(t('price_calculator.step_2.error_message_fetch'));
			} finally {
				setLoading(false);
			}
		};

		fetchContainers();
	}, []); // Se ejecuta al montar el componente

	const fetchPorts = async (): Promise<GroupedPorts[]> => {
		try {
			const response = await getApiPortsCountries();
			if (!response || !response.results) return [];

			return response.results
				.map((country) => ({
					country: country.name ?? 'Desconocido',
					ports:
						country.ports?.map((port) => ({
							id: port.id,
							name: port.name,
							descriptionEn: port.descriptionEn ?? '',
							descriptionEs: port.descriptionEs ?? '',
						})) ?? [],
				}))
				.filter((group) => group.ports.length > 0); // Filtrar países sin puertos
		} catch (error: any) {
			console.error('Error fetching ports:', error);
			setError(t('price_calculator.step_2.error_message_fetch'));
			return [];
		}
	};

	const fetchAirports = async (): Promise<GroupedAirports[]> => {
		try {
			const response = await getApiAirportGetAll();
			if (!response || !response.results) return [];

			// Group airports by country
			const airportsByCountry = response.results.reduce(
				(acc, airport) => {
					const countryName = airport.countryName ?? 'Desconocido';
					if (!acc[countryName]) {
						acc[countryName] = [];
					}
					acc[countryName].push({
						id: airport.id,
						name: airport.name,
						descriptionEn: airport.descriptionEn ?? '',
						descriptionEs: airport.descriptionEs ?? '',
						countryCode: airport.countryCode,
						countryId: airport.countryId,
						countryName: airport.countryName,
						latitude: airport.latitude,
						longitude: airport.longitude,
					});
					return acc;
				},
				{} as Record<string, any[]>,
			);

			return Object.entries(airportsByCountry).map(([country, airports]) => ({
				country,
				airports,
			}));
		} catch (error: any) {
			console.error('Error fetching airports:', error);
			setError(t('price_calculator.step_2.error_message_fetch'));
			return [];
		}
	};

	useEffect(() => {
		const loadPorts = async () => {
			try {
				setLoadingPorts(true);
				if (transportation === 'maritimo' || transportation === 'multimodal') {
					const fetchedPorts = await fetchPorts();
					setGroupedPorts(fetchedPorts);
				} else if (transportation === 'aereo') {
					const fetchedAirports = await fetchAirports();
					setGroupedAirports(fetchedAirports);
				}
			} catch (error: any) {
				console.error('Error fetching ports/airports:', error);
				setError(t('price_calculator.step_2.error_message_fetch'));
			} finally {
				setLoadingPorts(false);
			}
		};

		loadPorts();
	}, [transportation]);

	// Api SeaRates GetP2P and GetD2D
	const portDestinationId = watch('portDestinationId');
	const containerId = watch('containerId');
	// Agregar variables para transporte aéreo
	const airTotalWeightPlusPackaging = watch('airTotalWeightPlusPackaging') as string;
	const airTarifCode = watch('airTarifCode') as string;

	const [loadingSeaRates, setLoadingSeaRates] = useState(false);
	const [errorSeaRates, setErrorSeaRates] = useState<string | null>(null);
	const [seaRatesData, setSeaRatesData] = useState<any>(null);

	// Función para verificar si los campos aéreos están completos
	const areAirFieldsComplete = () => {
		return !!(formTwoValues?.airportId && formTwoValues?.airTotalWeightPlusPackaging && formTwoValues?.airTarifCode);
	};

	// Función para calcular tarifas aéreas
	const handleCalculateAirRates = async () => {
		const airportId = formTwoValues?.airportId;
		const airTotalWeightPlusPackaging = formTwoValues?.airTotalWeightPlusPackaging;
		const airTarifCode = formTwoValues?.airTarifCode;

		if (!airportId || !airTotalWeightPlusPackaging || !airTarifCode) {
			const missingFields = [];
			if (!airportId) missingFields.push(t('price_calculator.step_2.portDestinationId.placeholder'));
			if (!airTotalWeightPlusPackaging)
				missingFields.push(t('price_calculator.step_2.airTotalWeightPlusPackaging.placeholder'));
			if (!airTarifCode) missingFields.push(t('price_calculator.step_2.airTarifCode.placeholder'));

			setErrorSeaRates(`${t('price_calculator.step_2.error_message_button')} ${missingFields.join(', ')}`);
			return;
		}

		setLoadingSeaRates(true);
		setErrorSeaRates(null);

		try {
			const formOneStr = localStorage.getItem('formOne');
			const formOne = formOneStr ? JSON.parse(formOneStr) : {};

			const requestData = {
				countryOriginId: formOne?.countryOriginId,
				airportDestinationId: airportId,
				weightKg: parseFloat(airTotalWeightPlusPackaging) || 0,
				volumeInCubicMeters: airTarifCode || 0,
			};

			const response = await calculateAirRates(requestData);

			if (response?.result?.routeTariffPrice) {
				setSeaRatesData(response.result);
				// Actualizar el valor del campo internationalFreightValueUsd con routeTariffPrice
				const updatedFormValues = {
					...formTwoValues,
					internationalFreightValueUsd: response.result.routeTariffPrice.toString(),
					routeTariffPrice: response.result.routeTariffPrice,
					co2Amount: response.result.co2Amount,
					dthcPrice: response.result.dthcPrice,
					impPrice: response.result.impPrice,
				};
				setFormTwoValues(updatedFormValues);
				setValue('internationalFreightValueUsd', response.result.routeTariffPrice.toString());
				// Validar el rango después de establecer el valor
				setValidRange(response.result.routeTariffPrice >= 2000 && response.result.routeTariffPrice <= 8000);
			}
		} catch (err: any) {
			console.error('Error calculating air rates:', err);
			setErrorSeaRates(err.message || 'Error al calcular tarifas aéreas');
		} finally {
			setLoadingSeaRates(false);
		}
	};
	const { state } = useAuth();
	const {
		data: remainingCalcs,
		loading: loadingCalcs,
		error: errorCalcs,
	} = useRemainingCalculations(process.env.REACT_APP_PRICE_CALCULATOR_ID || '353');

	// Función para calcular tarifas aéreas usando la API real
	const calculateAirRates = async (requestData: {
		countryOriginId?: string;
		airportDestinationId?: string;
		volumeInCubicMeters?: number;
		weightKg?: number;
	}) => {
		try {
			const response = await postApiSeaRatesGetP2PAir(requestData);
			return response;
		} catch (error) {
			console.error('Error calling air rates API:', error);
			throw error;
		}
	};

	useEffect(() => {
		if (state?.isAuthenticated && remainingCalcs && remainingCalcs.remaining > 0) {
			// Lógica para transporte marítimo
			if (portDestinationId && containerId && transportation === 'maritimo') {
				// Verificar si ya tenemos datos completos de la API (routeTariffPrice, co2Amount, etc.)
				const hasCompleteApiData = formTwoValues?.routeTariffPrice && formTwoValues?.co2Amount;

				// Verificar si formOne cambió
				const formOneChanged = localStorage.getItem('formOneChanged') === 'true';
				// console.log('=== DEBUG FORMONE CHANGED ===');
				// console.log('formOneChanged from localStorage:', localStorage.getItem('formOneChanged'));
				// console.log('formOneChanged boolean:', formOneChanged);

				// Verificar si los valores han cambiado respecto a los datos originales del localStorage
				const hasChangedValues = originalFormValues
					? originalFormValues.portDestinationId !== portDestinationId || originalFormValues.containerId !== containerId
					: false;

				// Verificar si estamos cargando datos del localStorage (no hacer llamada en este caso)
				const isLoadingFromLocalStorage = !originalFormValues && formTwoValues && formTwoValues.portDestinationId;

				// Verificar si los valores actuales coinciden con los del localStorage
				const valuesMatchLocalStorage = originalFormValues
					? originalFormValues.portDestinationId === portDestinationId &&
						originalFormValues.containerId === containerId &&
						originalFormValues.meansTransportation === transportation
					: false;

				// Hacer la llamada si:
				// 1. No tenemos datos completos de la API Y
				// 2. (Los valores han cambiado O formOne cambió O no estamos cargando del localStorage) Y
				// 3. Los valores no coinciden exactamente con los del localStorage
				const shouldMakeApiCall =
					!hasCompleteApiData &&
					(hasChangedValues || formOneChanged || !isLoadingFromLocalStorage) &&
					!valuesMatchLocalStorage;

				// console.log('=== DEBUG API CALL LOGIC ===');
				// console.log('portDestinationId:', portDestinationId);
				// console.log('containerId:', containerId);
				// console.log('transportation:', transportation);
				// console.log('formTwoValues exists:', !!formTwoValues);
				// console.log('hasCompleteApiData:', hasCompleteApiData);
				// console.log('hasChangedValues:', hasChangedValues);
				// console.log('isLoadingFromLocalStorage:', isLoadingFromLocalStorage);
				// console.log('valuesMatchLocalStorage:', valuesMatchLocalStorage);

				if (shouldMakeApiCall) {
					// console.log('Haciendo llamada a la API - Datos nuevos o valores cambiados');
					// console.log('originalFormValues', originalFormValues);
					// console.log('formTwoValues', formTwoValues);
					// console.log('Valores actuales:', { portDestinationId, containerId });
					// console.log('formOneChanged:', formOneChanged);
					// console.log('hasChangedValues:', hasChangedValues);

					const fetchP2P = async () => {
						setLoadingSeaRates(true);
						setErrorSeaRates(null);
						try {
							const formOneStr = localStorage.getItem('formOne');
							const formOne = formOneStr ? JSON.parse(formOneStr) : {};
							const requestData = {
								countryOriginId: formOne?.countryOriginId,
								portDestinationId,
								containerTypeId: containerId,
							};
							// console.log('Request data:', requestData);
							const response = await postApiSeaRatesGetP2P(requestData);
							// console.log('P2P Response:', response);
							if (response?.result?.routeTariffPrice) {
								setSeaRatesData(response.result);
								// Actualizar el valor del campo internationalFreightValueUsd con routeTariffPrice
								const updatedFormValues = {
									...formTwoValues,
									portDestinationId,
									containerId,
									internationalFreightValueUsd: response.result.routeTariffPrice.toString(),
									routeTariffPrice: response.result.routeTariffPrice,
									co2Amount: response.result.co2Amount,
									dthcPrice: response.result.dthcPrice,
									impPrice: response.result.impPrice,
								};
								setFormTwoValues(updatedFormValues);
								setValue('internationalFreightValueUsd', response.result.routeTariffPrice.toString());
								// Validar el rango después de establecer el valor
								setValidRange(response.result.routeTariffPrice >= 2000 && response.result.routeTariffPrice <= 8000);

								// Limpiar la flag formOneChanged después de hacer la llamada exitosamente
								if (formOneChanged) {
									localStorage.removeItem('formOneChanged');
									console.log('formOneChanged limpiado después de llamada exitosa');
								}
							}
						} catch (err: any) {
							console.error('Error fetching P2P:', err);
							setErrorSeaRates(err.message || 'Error desconocido');
						} finally {
							setLoadingSeaRates(false);
						}
					};
					fetchP2P();
				} else {
					// console.log('No se hace llamada a la API - Usando datos del localStorage');
					// console.log('hasChangedValues:', hasChangedValues);
					// console.log('formOneChanged:', formOneChanged);
					// console.log('hasCompleteApiData:', hasCompleteApiData);
					// console.log('isLoadingFromLocalStorage:', isLoadingFromLocalStorage);
					// console.log('valuesMatchLocalStorage:', valuesMatchLocalStorage);
					// console.log('shouldMakeApiCall:', shouldMakeApiCall);

					// Si hay datos del localStorage y no han cambiado, establecer el rango válido basado en los datos existentes
					const freightValue = Number(formTwoValues.internationalFreightValueUsd);
					setValidRange(freightValue >= 2000 && freightValue <= 8000);

					// Si tenemos los datos de la API (routeTariffPrice, etc.), los usamos
					if (formTwoValues?.routeTariffPrice) {
						setSeaRatesData({
							routeTariffPrice: formTwoValues.routeTariffPrice,
							co2Amount: formTwoValues.co2Amount,
							dthcPrice: formTwoValues.dthcPrice,
							impPrice: formTwoValues.impPrice,
						});
					}
				}
			}
		}
	}, [portDestinationId, containerId, transportation, state?.isAuthenticated, remainingCalcs]);

	useEffect(() => {
		window.scrollTo({ top: 0, behavior: 'smooth' });
	}, []);

	if (loadingPorts) {
		return <CircularLoading />;
	}

	if (loading) return <CircularLoading />;

	if (error) {
		return (
			<div className="errorcalculator">
				<div className="errorcalculator__icon">
					<IconError />
				</div>

				<p className="errorcalculator__txt">{error}</p>
			</div>
		);
	}

	return (
		<form onSubmit={handleSubmit(onSubmit)}>
			<div className="exporter-test__read-only">
				<div className="price-calculator__container-main">
					<div className="price-calculator___inner">
						<div className="price-calculator___container">
							<div className="row">
								<div className="col-12">
									<span
										className="price-calculator__container-title"
										style={{ marginBottom: validateRange ? '0' : '24px' }}>
										{t('price_calculator.step_2.subtitle')}
									</span>
									<CustomToolTip
										title=""
										tooltipText={t('price_calculator.step_2.subtitle_tooltip')}>
										<FontAwesomeIcon
											icon={faCircleInfo}
											style={{ fontSize: '15px', marginLeft: '8px' }}
										/>
									</CustomToolTip>
								</div>
							</div>
							{/* ! state?.isAuthenticated && !remainingCalcs  */}
							{!validRange && !state?.isAuthenticated && !remainingCalcs && (
								<CustomMessage
									background={'#F4B400'}
									width="100%"
									textMessage={t('price_calculator.step_2.subtitle_tooltip_2', {
										rangeTo: DO?.flete_range?.rangeTo,
										rangeFrom: DO?.flete_range?.rangeFrom,
									})}
								/>
							)}

							<div className="row price-calculator__row mb-4">
								<div className="col-12 col-sm-12 col-md-6">
									<div className="label-container">
										<label className="label-input">
											{t('price_calculator.step_2.meansTransportation.placeholder')}
										</label>
										<select
											{...register('meansTransportation', { required: true })}
											value={formTwoValues?.meansTransportation || ''}
											onChange={handleChangeMeansTransportation}
											className="label-container__input input-label-select">
											<option
												value=""
												disabled>
												{t('select.placeholder')}
											</option>

											<option value="maritimo">{t('price_calculator.step_2.meansTransportation.maritimo')}</option>
											{/* <option value="multimodal">Multimodal</option> */}
											<option value="aereo">{t('price_calculator.step_2.meansTransportation.aereo')}</option>
										</select>
										<IconDropDownArrow className="input-label-select-arrow" />
									</div>

									{!!errors?.meansTransportation && (
										<div className="error-container">
											<InfoIcon fill="#F8A0D2" />
											<div className="error-message">{t('price_calculator.step_2.error_message')}</div>
										</div>
									)}
								</div>

								{transportation === 'aereo' && (
									<div className="col-12 col-sm-12 col-md-6">
										<div className="price-calculator__field-with-icon">
											<div className="label-container">
												<label className="label-input">
													{t('price_calculator.step_2.portDestinationId.placeholder')}
												</label>
												<select
													{...register('airportId', { required: true })}
													value={formTwoValues?.airportId || ''}
													onChange={(e) => {
														const selectedPortId = e.target.value;
														// Buscar el nombre del puerto seleccionado
														let selectedPortName = '';
														groupedPorts.forEach((group) => {
															const port = group.ports.find((p) => p.id === selectedPortId);
															if (port) {
																selectedPortName =
																	(i18n.language === 'es'
																		? port.descriptionEs || port.name
																		: port.descriptionEn || port.name) || '';
															}
														});

														// Verificar si el puerto de destino cambió
														const currentPortName = formTwoValues?.airPortName || '';
														if (currentPortName !== selectedPortName) {
															// Establecer bandera para indicar que el puerto de destino cambió
															localStorage.setItem('formTwoChanged', 'true');
														}

														setFormTwoValues({
															...formTwoValues,
															airportId: selectedPortId,
															airPortName: selectedPortName,
														});
													}}
													className="label-container__input input-label-select">
													<option
														value=""
														disabled>
														{t('select.placeholder')}
													</option>

													{loadingPorts ? (
														<option disabled>Cargando...</option>
													) : groupedAirports.length > 0 ? (
														groupedAirports.map((group) => (
															<optgroup
																key={group.country}
																label={group.country}>
																{group.airports.map((airport) => (
																	<option
																		key={airport.id}
																		value={airport.id}
																		className="price-calculator__select-font">
																		{i18n.language === 'es'
																			? `${airport.name || ''} ${airport.descriptionEs ? `- ${airport.descriptionEs}` : ''}`.trim() ||
																				'Sin descripción'
																			: `${airport.name || ''} ${airport.descriptionEn ? `- ${airport.descriptionEn}` : ''}`.trim() ||
																				'Sin descripción'}
																	</option>
																))}
															</optgroup>
														))
													) : (
														<option disabled>No se encontraron aeropuertos</option>
													)}
												</select>
												<IconDropDownArrow className="input-label-select-arrow" />
											</div>

											<div style={{ marginLeft: '8px' }}>
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_2.portDestinationId.tooltip')}>
													<FontAwesomeIcon
														icon={faCircleQuestion}
														className="font-size: 18px"
													/>
												</CustomToolTip>
											</div>
										</div>
										{!!errors?.airportId && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_2.error_message')}</div>
											</div>
										)}
									</div>
								)}

								{(transportation === 'maritimo' || transportation === '') && (
									<div className="col-12 col-sm-12 col-md-6">
										<div className="price-calculator__field-with-icon">
											<div className="label-container">
												<label className="label-input">
													{t('price_calculator.step_2.portDestinationId.placeholder')}
												</label>
												<select
													{...register('portDestinationId', { required: true })}
													value={formTwoValues?.portDestinationId || ''}
													onChange={(e) => {
														const selectedPortId = e.target.value;
														// Buscar el nombre del puerto seleccionado
														let selectedPortName = '';
														groupedPorts.forEach((group) => {
															const port = group.ports.find((p) => p.id === selectedPortId);
															if (port) {
																selectedPortName =
																	(i18n.language === 'es'
																		? port.descriptionEs || port.name
																		: port.descriptionEn || port.name) || '';
															}
														});

														// Verificar si el puerto de destino cambió
														const currentPortName = formTwoValues?.portDestinationName || '';
														if (currentPortName !== selectedPortName) {
															// Establecer bandera para indicar que el puerto de destino cambió
															localStorage.setItem('formTwoChanged', 'true');
														}

														setFormTwoValues({
															...formTwoValues,
															portDestinationId: selectedPortId,
															portDestinationName: selectedPortName,
														});
													}}
													className="label-container__input input-label-select">
													<option
														value=""
														disabled>
														{t('select.placeholder')}
													</option>

													{loadingPorts ? (
														<option disabled>Cargando...</option>
													) : groupedPorts.length > 0 ? (
														groupedPorts.map((group) => (
															<optgroup
																key={group.country}
																label={group.country}>
																{group.ports.map((port) => (
																	<option
																		key={port.id}
																		value={port.id}
																		className="price-calculator__select-font">
																		{i18n.language === 'es'
																			? port.descriptionEs || 'Sin descripción'
																			: port.descriptionEn || 'Sin descripción'}
																	</option>
																))}
															</optgroup>
														))
													) : (
														<option disabled>No se encontraron puertos</option>
													)}
												</select>
												<IconDropDownArrow className="input-label-select-arrow" />
											</div>

											<div style={{ marginLeft: '8px' }}>
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_2.portDestinationId.tooltip')}>
													<FontAwesomeIcon
														icon={faCircleQuestion}
														className="font-size: 18px"
													/>
												</CustomToolTip>
											</div>
										</div>
										{!!errors?.portDestinationId && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_2.error_message')}</div>
											</div>
										)}
									</div>
								)}
							</div>
							{transportation === 'aereo' && (
								<div className="row price-calculator__row mb-4">
									<div className="col-12 col-sm-12 col-md-6 label-container">
										<input
											{...register('airTotalWeightPlusPackaging', { required: true })}
											type="number"
											className="price-calculator__textbox-general-col2"
											value={
												!!formTwoValues?.airTotalWeightPlusPackaging ? formTwoValues?.airTotalWeightPlusPackaging : ''
											}
											onChange={(e) => {
												if (e.target.value === '' || numberRegex.test(e.target.value)) {
													// Verificar si el peso cambió
													const currentWeight = formTwoValues?.airTotalWeightPlusPackaging || '';
													if (currentWeight !== e.target.value) {
														// Establecer bandera para indicar que el peso cambió
														localStorage.setItem('formTwoChanged', 'true');
													}

													setFormTwoValues({
														...formTwoValues,
														airTotalWeightPlusPackaging: formatNumber(e.target.value),
													});
												}
											}}
										/>
										<label className="label-input">
											{t('price_calculator.step_2.airTotalWeightPlusPackaging.placeholder')}
										</label>
										{!!errors?.airTotalWeightPlusPackaging && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_2.error_message')}</div>
											</div>
										)}
									</div>
									<div className="col-12 col-sm-12 col-md-6 label-container">
										<input
											type="number"
											className="price-calculator__textbox-general-col2"
											value={!!formTwoValues?.airTarifCode ? formTwoValues?.airTarifCode : ''}
											{...register('airTarifCode', { required: true })}
											onChange={(e) => {
												if (e.target.value === '' || numberRegex.test(e.target.value)) {
													// Verificar si el código tarifario cambió
													const currentTarifCode = formTwoValues?.airTarifCode || '';
													if (currentTarifCode !== e.target.value) {
														// Establecer bandera para indicar que el código tarifario cambió
														localStorage.setItem('formTwoChanged', 'true');
													}

													setFormTwoValues({ ...formTwoValues, airTarifCode: formatNumber(e.target.value) });
												}
											}}
										/>
										<label className="label-input">{t('price_calculator.step_2.airTarifCode.placeholder')}</label>
										{!!errors?.airTarifCode && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_2.error_message')}</div>
											</div>
										)}
									</div>
								</div>
							)}

							<div className="row price-calculator__row mb-4">
								{transportation === 'maritimo' && (
									<div className="col-12 col-sm-12 col-md-6">
										<div className="price-calculator__field-with-icon">
											<div className="label-container">
												<label className="label-input">{t('price_calculator.step_2.containerId.placeholder')}</label>
												<select
													{...register('containerId', { required: true })}
													value={formTwoValues?.containerId || ''}
													onChange={(e) => {
														const selectedContainerId = e.target.value;

														// Verificar si el contenedor cambió
														const currentContainerId = formTwoValues?.containerId || '';
														if (currentContainerId !== selectedContainerId) {
															// Establecer bandera para indicar que el contenedor cambió
															localStorage.setItem('formTwoChanged', 'true');
														}

														setFormTwoValues({ ...formTwoValues, containerId: selectedContainerId });
													}}
													className="label-container__input input-label-select">
													<option
														value=""
														disabled
														hidden>
														{t('select.placeholder')}
													</option>
													{containers.map((option) => (
														<option
															key={option.id}
															value={option.id}>
															{i18n.language === 'es'
																? option.descriptionEs || 'Sin descripción'
																: option.descriptionEn || 'Sin descripción'}
														</option>
													))}
												</select>
												<IconDropDownArrow className="input-label-select-arrow" />
											</div>
											<div style={{ marginLeft: '8px' }}>
												<CustomToolTip
													title=""
													tooltipText={t('price_calculator.step_2.containerId.tooltip')}>
													<FontAwesomeIcon
														icon={faCircleQuestion}
														className="font-size: 18px"
													/>
												</CustomToolTip>
											</div>
										</div>
										{!!errors?.containerId && (
											<div className="error-container">
												<InfoIcon fill="#F8A0D2" />
												<div className="error-message">{t('price_calculator.step_2.error_message')}</div>
											</div>
										)}
									</div>
								)}
								{transportation !== '' && (
									<div className="col-12 col-sm-12 col-md-6">
										{loadingSeaRates ? (
											<div className="price-calculator__field-loading">
												<CircularLoading />
											</div>
										) : (
											<>
												<div className="price-calculator__field-with-icon label-container container_input__pro">
													<input
														type="number"
														className="price-calculator__textbox-general-col2"
														{...register('internationalFreightValueUsd', { required: true })}
														value={
															!!formTwoValues?.internationalFreightValueUsd
																? formTwoValues?.internationalFreightValueUsd
																: ''
														}
														onChange={(e) => {
															if (e.target.value === '' || numberRegex.test(e.target.value)) {
																setFormTwoValues({
																	...formTwoValues,
																	internationalFreightValueUsd: formatNumber(e.target.value),
																});
															}
														}}
														onBlur={(e) =>
															setValidRange(Number(e.target.value) >= 2000 && Number(e.target.value) <= 8000)
														}
													/>
													<label className="label-input">
														{t('price_calculator.step_2.internationalFreightValueUsd.placeholder')}
													</label>
													{state?.isAuthenticated &&
														remainingCalcs &&
														remainingCalcs.remaining &&
														transportation === 'aereo' && (
															<button
																type="button"
																onClick={handleCalculateAirRates}
																className={`btn-calculate-air ${
																	loadingSeaRates || !areAirFieldsComplete() ? 'disabled' : ''
																}`}>
																{t('price_calculator.step_2.internationalFreightValueUsd.button')}
															</button>
														)}

													<div style={{ marginLeft: '8px' }}>
														<CustomToolTip
															title=""
															tooltipText={t('price_calculator.step_2.internationalFreightValueUsd.tooltip')}>
															<FontAwesomeIcon
																icon={faCircleQuestion}
																className="font-size: 18px"
															/>
														</CustomToolTip>
													</div>
												</div>
												<div className="label__pro">
													<IconPro
														width={13}
														height={10}
														fill="#BBC2C7"
													/>
													{t('price_calculator.label_pro.description')}
												</div>
												{!!errors?.internationalFreightValueUsd &&
													errors?.internationalFreightValueUsd.type === 'required' && (
														<div className="error-container">
															<InfoIcon fill="#F8A0D2" />
															<div className="error-message">{t('price_calculator.step_2.error_message')}</div>
														</div>
													)}
												{errorSeaRates && (
													<div className="error-container">
														<InfoIcon fill="#F8A0D2" />
														<div className="error-message">{errorSeaRates}</div>
													</div>
												)}
											</>
										)}
									</div>
								)}
							</div>
						</div>
					</div>
				</div>
				<div className="price-calculator__container-footer">
					<div
						className="price-calculator__footer-link"
						onClick={handleClickOpen}>
						{t('price_calculator.footer.link')}
					</div>
					<button
						type="submit"
						className="price-calc__next">
						<div className="btn-test-next-text">{t('price_calculator.footer.button')}</div>
						<FontAwesomeIcon
							icon={faArrowRight}
							color="black"
						/>
					</button>
				</div>
			</div>
		</form>
	);
};

export default PriceCalculatorStepTwo;
